/**
  * @class meleDiagnosticsIcons
  * @extends meleElement
  * @description
  *
  * # Purpose
  * Reusable element `<mele-diagnostics-icons>` that shows a row of diagnostic
  * "badge" icons (Wi-Fi, VPN-P/B, Server-P/B, mBox/peering, GPS, Time sync) with
  * ok/bad/pending/off state and a tooltip. The visual logic (probe -> icon
  * mapping, icon variant selection, tooltip) was extracted from mmodSkLogin here
  * so it can be reused on other screens (login, enrollment, diagnostics).
  *
  * # What the element is NOT
  * - It is NOT a listener and does NOT subscribe to any channels (#DIAGNOSTICS
  *   etc.). The data is sent to it by the parent module (mmodSkLogin,
  *   mmodSkEnrollment, ...), which is the subscriber. The element is purely
  *   presentational + keeps a small state model (the latest probe values) for
  *   correct rendering.
  *
  * # Configuration (attributes)
  * - `probes` {string} - comma-separated list of icons to show, e.g.
  *   "wifi,vpn_p,vpn_b,server_p,server_b,peering,gps,time,battery". The order in
  *   the list = the display order. An icon not in the list is not rendered and
  *   its data is silently ignored (filter by `probes`).
  * - `layout` {string} - "horizontal" (default) | "vertical". Just a CSS switch
  *   for the row direction; the element does no auto-detect, the direction is
  *   decided by the parent based on the place of use.
  * - `time_tolerance` {integer} default 5000 - max |peer-own| drift in ms before
  *   the Time sync icon flips to "bad".
  * - `data` {object} - channel for INCOMING probe data. For each #DIAGNOSTICS
  *   message the parent sets `el.data = { probe, data, own }`:
  *     - `probe` {string} - probe name ('pc_reach','ws','gps','time',
  *       'vpn_patrol','vpn_mbox')
  *     - `data` {object} - probe payload (params.data from the diagnostics message)
  *     - `own` {boolean} - true if the message comes from OUR OWN application (not
  *       from a peer/mBox). The parent computes it (sender.app_id === this.app_id),
  *       because the element does not know app_id.
  *   meleElement object-attribute coerce auto-parses JSON, so the data can also be
  *   sent via the attribute; normally the parent sets it via the property though.
  *
  * # Probe -> icon mapping
  * - 'pc_reach'    -> wifi      (trojstavovy: ping na vozidlovy PC = "je wifi",
  *                               nslookup APN hosta = "je APN"; red/yellow/green
  *                               podla `data.state`)
  * - 'ws' (own)    -> peering + server_p   (patrol -> mBox and patrol -> mServer)
  * - 'ws' (peer)   -> server_b             (mBox -> mServer)
  * - 'gps'         -> gps
  * - 'time'        -> time     (accumulates own+peer time, compares the drift)
  * - 'vpn_patrol'  -> vpn_p    (4-farebne podla vpncli state: Connected=zelena,
  *                              Connecting=zlta, Unknown=siva, Disconnected/ziadne
  *                              data=cervena; + wifi gate: kym pc_reach nie je
  *                              'ready', VPN-P je vzdy cervena - viz renderVpnSide)
  * - 'vpn_mbox'    -> vpn_b    (2-farebne, connected bool)
  * - 'battery'     -> battery  (own only - mPatrol local battery, not mBox)
  */

import meleElement from 'meleElement'

// Icon catalog: for each key (= badge identifier) defines the icon "base" (the
// sys.<thing>.0 / .1 pair = off/on with built-in red/green color - only the
// variant changes), the label (translation or plain text) and the default
// tooltip for the initial "off" state. The keys correspond to the values in the
// `probes` attribute.
const ICONS = {
  wifi: { base: 'sys.wifi', label: '{{diag.wifi}}', title: 'Wi-Fi' },
  vpn_p: { base: 'sys.vpn', label: 'VPN-P', title: 'VPN-P' },
  vpn_b: { base: 'sys.vpn', label: 'VPN-B', title: 'VPN-B' },
  server_p: { base: 'sys.server', label: 'Server-P', title: 'Server-P' },
  server_b: { base: 'sys.server', label: 'Server-B', title: 'Server-B' },
  peering: { base: 'sys.peers', label: '{{diag.peering}}', title: 'mBox' },
  gps: { base: 'sys.gps', label: '{{diag.gps}}', title: 'GPS' },
  time: { base: 'sys.time', label: '{{diag.time_sync}}', title: 'Time sync' },
  // Battery has stage 1..4 variants instead of the .0/.1 (off/on) pattern -
  // the `base: 'sys.battery'` is the prefix; the stage is appended by
  // renderBattery (e.g. 'sys.battery.3' or 'sys.battery.charging.3').
  battery: { base: 'sys.battery', label: '{{diag.battery}}', title: 'Battery' },
}

const PROPERTIES = _.extend({}, {
  probes: { type: 'string', description: 'comma-separated list of icons to show (wifi,vpn_p,vpn_b,server_p,server_b,peering,gps,time)' },
  layout: { type: 'string', description: 'horizontal (default) | vertical - row direction only', default: 'horizontal' },
  time_tolerance: { type: 'integer', description: 'max |peer-own| time drift in ms before Time sync flips to bad', default: 5000 },
  data: { type: 'object', description: 'incoming probe message channel: { probe, data, own } set by the parent module per #DIAGNOSTICS message' },
}, meleElement._properties)

export default class meleDiagnosticsIcons extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates the internal DOM structure. The rungs (individual badges) are built
  * only in `probesChanged` (they need the `probes` list, which arrives after
  * init() - see meleElement.connectedCallback: init() first, then the attributes
  * are replayed). Here we just initialize the small probe state model.
  */
  init() {
    super.init()
    // Last pc_reach payload (the Wi-Fi badge is rendered from it).
    this._pc_reach = null
    // Last vpn_patrol payload. Kept so VPN-P can be re-rendered when the Wi-Fi
    // state changes (VPN-P is gated on Wi-Fi being green — see renderVpnSide),
    // not only when a fresh vpn_patrol message arrives.
    this._vpn_p = null
    // Time-sync accumulation: whether own and peer time have arrived, the last
    // peer time and when it was received (to estimate the current peer time).
    this._own_time_seen = false
    this._peer_time_seen = false
    this._peer_time = 0
    this._peer_time_at = 0
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * On a change of the `probes` list, builds (rebuilds) the row of badges and sets
  * each to the initial "off" state with a default tooltip. An unknown key in the
  * list is skipped.
  * @param {string} value - comma-separated list of icon keys
  */
  probesChanged(value) {
    const keys = String(value ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(k => k && ICONS[k])

    // Rebuild the content from scratch. The element has no internal structure
    // other than the rungs.
    this.innerHTML = ''
    for(const key of keys) {
      const meta = ICONS[key]
      this.appendChildren({
        tag: 'div',
        key,
        class: 'diag-rung',
        children: [
          { tag: 'mele-image', icon: meta.base + '.0' },
          { tag: 'mele-text', label: meta.label },
        ],
      })
      // Iniciálny stav (+ default tooltip). VPN-P zámerne CERVENA uz pred prvym
      // pollom (nie stlmene 'off'): kym nemame potvrdene Connected, VPN sa
      // povazuje za nepripojenu. Ostatne ikonky startuju v stlmenom 'off'.
      this.setIcon(key, key === 'vpn_p' ? 'bad' : 'off', meta.title)
    }
  } // probesChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Processes a single incoming probe message set by the parent. Based on `probe`
  * it updates the relevant icon (or several, e.g. 'ws' own -> peering + server_p).
  * Icons that are not in `probes` are silently ignored (setIcon does not find the
  * rung and returns).
  * @param {object} value - { probe, data, own }
  */
  dataChanged(value) {
    if(!value || typeof value !== 'object') return
    const probe = value.probe
    const payload = value.data
    const own = value.own !== false // default true if the parent did not specify
    switch(probe) {
      // pc_reach mení Wi-Fi ikonku A ZAROVEN VPN-P (tá je gate-nutá na Wi-Fi:
      // ak wifi nie je zelena, VPN-P musi byt cervena - preto ju prekreslime aj
      // ked sa zmeni wifi, nielen pri novom vpn_patrol).
      case 'pc_reach': this._pc_reach = payload; this.renderWifi(); this.renderVpnSide('vpn_p', 'VPN-P', this._vpn_p); break
      // Own ws drives peering (patrol->mBox) + Server-P (patrol->mServer); the
      // peer's ws drives Server-B (mBox->mServer). Separate renderers prevent
      // the mBox ws from overwriting the patrol badges.
      case 'ws': own ? this.renderWs(payload) : this.renderWsPeer(payload); break
      case 'gps': this.renderGps(payload); break
      case 'time': this.renderTime(payload, own); break
      case 'vpn_patrol': this._vpn_p = payload; this.renderVpnSide('vpn_p', 'VPN-P', payload); break
      case 'vpn_mbox': this.renderVpnSide('vpn_b', 'VPN-B', payload); break
      // Battery is mPatrol-local; peer (mBox) battery is a separate concern.
      case 'battery': if(own) this.renderBattery(payload); break
    }
  } // dataChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sets one badge icon's variant + tooltip according to the state. The
  * sys.<thing>.0 / .1 pairs carry their own off/on (red/green) color, so we only
  * switch the variant. The wrapper gets the `diag-off` class for the dim
  * pending/off state. If the rung for the given key does not exist (it is not in
  * `probes`), the method silently returns - that is the filter by the `probes`
  * list.
  * @param {string} key - icon key (wifi, vpn_p, ...)
  * @param {string} status - 'ok' (.1 zelena) | 'warn' (.2 zlta) | 'unknown' (.3 siva) | 'bad' (.0 cervena) | 'pending' | 'off' (posledne dva = .0 + stlmenie)
  * @param {string} title - tooltip text
  */
  setIcon(key, status, title) {
    const rung = this.find('@' + key)
    if(!rung) return
    const img = rung.querySelector('mele-image')
    const base = ICONS[key] && ICONS[key].base
    if(img && base) {
      // Farebne varianty: `.1` = zelena (ok), `.2` = jantarova/zlta (warn),
      // `.3` = siva (unknown), vsetko ostatne (bad/pending/off) = `.0` cervena.
      // Varianty `.2`/`.3` musia byt pre danu ikonu definovane v icons.htm
      // (zatial: `.2` = sys.wifi/sys.vpn, `.3` = sys.vpn). Pre ikonu bez daneho
      // variantu treba prislusny status nepouzivat - inak by mele-image ukazalo
      // nedefinovanu ikonu. 'warn'/'unknown' sa dnes pouzivaju len tam, kde
      // varianty existuju (wifi, vpn).
      const variant = status === 'ok' ? '.1' : status === 'warn' ? '.2' : status === 'unknown' ? '.3' : '.0'
      img.setAttribute('icon', base + variant)
    }
    rung.classList.toggle('diag-off', status === 'off' || status === 'pending')
    rung.setAttribute('title', _.toString(title))
  } // setIcon

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Wi-Fi badge - trojstavovy, riadeny probom `pc_reach` (msvcSkDiagnostic ho
  * rozsiruje: ping na vozidlovy PC = "je wifi", nslookup APN hosta = "je APN").
  * Farba podla `pc.state`:
  *   - no data yet -> pending (stlmena)  - prob este nedobehol prvy tik
  *   - !enabled    -> off (stlmena)      - ping nie je nakonfigurovany (pc_host)
  *   - 'no_wifi'   -> bad (CERVENA)      - ping zlyhal -> nie je wifi
  *   - 'no_apn'    -> warn (ZLTA)        - wifi je, ale nslookup zlyhal -> bez APN
  *   - 'ready'     -> ok (ZELENA)        - wifi aj APN funguju
  *
  * Jediny producent `pc_reach` (msvcSkDiagnostic.probePcReach) vzdy nastavi
  * `state` (alebo vrati enabled:false), takze payload s enabled:true bez `state`
  * nevznika - neznamy/chybajuci state preto len bezpecne spadne do 'pending'.
  */
  renderWifi() {
    const pc = this._pc_reach
    if(!pc) {
      this.setIcon('wifi', 'pending', 'Wi-Fi  waiting...')
      return
    }
    if(!pc.enabled) {
      this.setIcon('wifi', 'off', 'Wi-Fi  pc_reach not configured')
      return
    }
    switch(pc.state) {
      case 'no_wifi':
        this.setIcon('wifi', 'bad', 'Wi-Fi  odpojene (ping zlyhal)')
        return
      case 'no_apn':
        this.setIcon('wifi', 'warn', 'Wi-Fi  bez APN' + (pc.apn_host ? '  ' + pc.apn_host : ''))
        return
      case 'ready': {
        const ip = Array.isArray(pc.apn_addresses) && pc.apn_addresses.length ? '  ' + pc.apn_addresses[0] : ''
        const lat = pc.latency_ms != null ? '  ' + pc.latency_ms + 'ms' : ''
        this.setIcon('wifi', 'ok', 'Wi-Fi' + (pc.apn_host ? '  APN ' + pc.apn_host : '') + ip + lat)
        return
      }
      default:
        // Neznamy/chybajuci state (nemalo by nastat - viz doc) -> bezpecne pending.
        this.setIcon('wifi', 'pending', 'Wi-Fi  waiting...')
    }
  } // renderWifi

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Own ws (mPatrol) - drives the peering (patrol->mBox) and Server-P
  * (patrol->mServer) badge. The mBox ws goes through renderWsPeer.
  * @param {object} data - ws probe payload { peering, server }
  */
  renderWs(data) {
    if(!data) return
    const peerOk = !!(data.peering && data.peering.connected)
    this.setIcon('peering', peerOk ? 'ok' : 'bad',
      'mBox' + (data.peering && data.peering.app_id ? '  ' + data.peering.app_id : ''))
    const srvOk = !!(data.server && data.server.connected)
    this.setIcon('server_p', srvOk ? 'ok' : 'bad',
      'Server-P' + (data.server && data.server.url ? '  ' + data.server.url : ''))
  } // renderWs

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Peer (mBox) ws - drives Server-B (mBox->mServer). We only care whether mBox
  * itself can reach the server.
  * @param {object} data - ws probe payload from the peer { server, ... }
  */
  renderWsPeer(data) {
    if(!data) return
    const srvOk = !!(data.server && data.server.connected)
    this.setIcon('server_b', srvOk ? 'ok' : 'bad',
      'Server-B' + (data.server && data.server.url ? '  ' + data.server.url : ''))
  } // renderWsPeer

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * GPS badge.
  *   - no data / available=false -> off
  *   - fix > 0                    -> ok
  *   - otherwise                  -> pending
  * @param {object} data - gps probe payload { available, fix, satellites }
  */
  renderGps(data) {
    if(!data || data.available === false) {
      this.setIcon('gps', 'off', 'GPS  no data')
      return
    }
    const fix = _.natural(data.fix)
    const sats = _.natural(data.satellites)
    const status = fix > 0 ? 'ok' : 'pending'
    this.setIcon('gps', status, 'GPS  fix=' + fix + ' sat=' + sats)
  } // renderGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Time-sync badge. The 'time' probe arrives from our own app (own=true) and from
  * peers (own=false). State:
  *   - "pending" until both sides have reported
  *   - "ok"      once both reported AND |peer-own| < time_tolerance
  *   - "bad"     once both reported and the drift exceeds the tolerance
  * @param {object} data - time probe payload { now, ... }
  * @param {boolean} own - true if this is our own time (not a peer)
  */
  renderTime(data, own) {
    if(!data) return
    if(own) {
      this._own_time_seen = true
    } else {
      this._peer_time_seen = true
      this._peer_time = _.natural(data.now)
      this._peer_time_at = Date.now()
    }
    if(!this._own_time_seen || !this._peer_time_seen) {
      this.setIcon('time', 'pending', 'Time sync  waiting...')
      return
    }
    const tolerance_ms = _.natural(this.time_tolerance) || 5000
    const peer_now_est = this._peer_time + (Date.now() - this._peer_time_at)
    const drift = Math.abs(Date.now() - peer_now_est)
    const status = drift < tolerance_ms ? 'ok' : 'bad'
    this.setIcon('time', status, 'Time sync  drift ' + drift + 'ms')
  } // renderTime

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * One VPN icon - patrol (vpn_p) or mBox (vpn_b). Each side has its own icon so a
  * single broken one does not mask the other working one.
  *
  * # VPN-P (Cisco, na tablete) - 4-farebne podla realneho `vpncli state`
  * VPN je samostatna sietova vrstva: moze spadnut aj pri funkcnej wifi+APN, tak
  * ikonka zrkadli jeho skutocny stav (nielen "connected/nie").
  *   - Connected               -> ZELENA (ok, sys.vpn.1)
  *   - Connecting / Reconnecting -> ZLTA (warn, sys.vpn.2) - GUI prave reconnectuje
  *   - Unknown                 -> SIVA (unknown, sys.vpn.3) - vpncli nevie urcit stav
  *   - Disconnected / ziadne data / pred prvym pollom -> CERVENA (bad, sys.vpn.0)
  *
  * VPN-P Wi-Fi gate (ma prednost pred farbou stavu): VPN-P NESMIE byt zelena
  * skor, nez je zelena Wi-Fi ikonka. Bez wifi+APN (pc_reach.state === 'ready')
  * sa VPN aj tak nemoze pripojit, a vpncli by mohlo hlasit Connected z tunela,
  * ktory prezil restart / auto-connect vpnagent-a - to by ukazovalo zelenu VPN-P
  * este pred zelenou wifi. Preto kym wifi nie je 'ready', VPN-P drzime CERVENU.
  *
  * # VPN-B (mBox) - povodne 2-stavove (connected bool)
  * VPN-B zavisi od konektivity mBoxu (nie tabletovej wifi) a beží z ineho probu
  * (vpn_mbox / systemd active|inactive), tak sa NEgatuje na wifi a ostava
  * dvojfarebne (connected=zelena, inak cervena; ziadne data=pending, !enabled=off).
  *
  * @param {string} key - 'vpn_p' or 'vpn_b'
  * @param {string} label - tooltip prefix ('VPN-P' / 'VPN-B')
  * @param {object} data - vpn probe payload { enabled, connected, state, error }
  */
  renderVpnSide(key, label, data) {
    // Wi-Fi gate pre VPN-P: kym wifi nie je 'ready', VPN-P je vzdy cervena
    // (bez ohladu na vpncli stav).
    if(key === 'vpn_p' && (!this._pc_reach || this._pc_reach.state !== 'ready')) {
      this.setIcon('vpn_p', 'bad', label + '  caka na Wi-Fi+APN')
      return
    }
    // VPN-P: farba podla realneho `vpncli state` (state je uz normalizovany v
    // msvcVpnCisco.parseState na Connected/Connecting/Disconnected/Unknown).
    if(key === 'vpn_p') {
      const state = String(data?.state || '')
      if(state === 'Connected') {
        this.setIcon('vpn_p', 'ok', label + '  Connected')
      } else if(state === 'Connecting' || state === 'Reconnecting') {
        this.setIcon('vpn_p', 'warn', label + '  ' + state)
      } else if(state === 'Unknown') {
        this.setIcon('vpn_p', 'unknown', label + '  Unknown')
      } else {
        // Disconnected, prazdny state (ziadne data / pred prvym pollom) aj cokolvek ine.
        this.setIcon('vpn_p', 'bad', label + '  ' + (state || 'no data'))
      }
      return
    }
    // VPN-B (mBox) - 2-stavove spravanie.
    if(!data) {
      this.setIcon(key, 'pending', label + '  waiting...')
      return
    }
    if(!data.enabled) {
      this.setIcon(key, 'off', label + '  not configured')
      return
    }
    if(data.connected) {
      this.setIcon(key, 'ok', label + '  ' + (data.state || 'ok'))
      return
    }
    this.setIcon(key, 'bad', label + '  ' + (data.state || data.error || 'down'))
  } // renderVpnSide

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Battery badge. Unlike the other rungs, battery has 4 stage icons
  * (sys.battery.1..4) + a charging overlay variant (sys.battery.charging.N), so
  * it can't use the .0/.1 setIcon helper - the icon attribute is set directly
  * here. The stage thresholds mirror the previous AppBar logic
  * (>=75 -> 4, >=50 -> 3, >=25 -> 2, else -> 1) and `diag-warn` blinks the rung
  * when the level is critical and the device is not charging.
  *   - no data / no battery        -> off (waiting / unsupported)
  *   - charging                    -> sys.battery.charging.<stage>, no warn
  *   - on battery, percent < 30    -> sys.battery.<stage> + diag-warn (blink)
  *   - on battery, percent >= 30   -> sys.battery.<stage>
  * @param {object} data - battery probe payload { hasBattery, isCharging, percent, ... }
  */
  renderBattery(data) {
    const rung = this.find('@battery')
    if(!rung) return
    const img = rung.querySelector('mele-image')
    if(!img) return
    if(!data || data.hasBattery === false) {
      img.setAttribute('icon', 'sys.battery.0')
      rung.classList.toggle('diag-off', true)
      rung.classList.toggle('diag-warn', false)
      rung.setAttribute('title', 'Battery  no data')
      return
    }
    const percent = _.natural(data.percent)
    const charging = !!(data.isCharging || data.acConnected)
    const stage = percent >= 75 ? 4 : percent >= 50 ? 3 : percent >= 25 ? 2 : 1
    const icon = charging ? ('sys.battery.charging.' + stage) : ('sys.battery.' + stage)
    img.setAttribute('icon', icon)
    rung.classList.toggle('diag-off', false)
    rung.classList.toggle('diag-warn', percent < 30 && !charging)
    rung.setAttribute('title', 'Battery  ' + percent + '%' + (charging ? '  charging' : ''))
  } // renderBattery
} // class meleDiagnosticsIcons

meleDiagnosticsIcons._properties = PROPERTIES
meleDiagnosticsIcons.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-diagnostics-icons', meleDiagnosticsIcons)
